import {Drivers} from "./Drivers";
import AttachmentStatus from "./enums/AttachmentStatus.js";
import AttachmentType from "./enums/AttachmentType";
import IAllPermitsReservations from "./interfaces/IAllPermitsReservations";
import IAttachments from "./interfaces/IAttachments.js";
import {Residencies} from "./Residencies";
import {AttachmentResult} from "./types/AttachmentResult.js";
import {EntityDocument} from "./types/EntityDocument";
import {PermitResult} from "./types/PermitResult.js";
import {Vehicles} from "./Vehicles";

function isInvalidInternal(entity: AttachmentResult): boolean {
	return entity.status === AttachmentStatus.Expired || entity.status === AttachmentStatus.Rejected;
}

function findAllInvalid(entity: IAttachments | undefined): AttachmentResult[] {
	if (entity == null) return [];
	const result: AttachmentResult[] = [];
	for (const x of entity.attachments) {
		if (isInvalidInternal(x)) {
			result.push(x);
		}
	}
	return result;
}

function createAttachmentEntries<T extends IAttachments, E extends "residency" | "driver" | "permit" | "reservation" | "vehicle" | "holder">(entities: T[], entityType: E): Array<{
	attachment: AttachmentResult;
	entityType: E;
	entity: T
}> {
	const result: Array<{ attachment: AttachmentResult; entityType: E; entity: T }> = [];
	for (const entity of entities) {
		for (const attachment of entity.attachments) {
			result.push({attachment, entityType, entity});
		}
	}
	return result;
}

function mapAttachmentsToEntityDocuments<T extends IAttachments, E extends "residency" | "driver" | "permit" | "reservation" | "vehicle" | "holder">(entity: T, entityType: E): Array<{
	attachment: AttachmentResult;
	entityType: E;
	entity: T
}> {
	const result: Array<{ attachment: AttachmentResult; entityType: E; entity: T }> = [];
	for (const attachment of entity.attachments) {
		result.push({attachment, entityType, entity});
	}
	return result;
}


class Attachments {
	static isInvalid(entity: AttachmentResult): boolean {
		return isInvalidInternal(entity);
	}

	static areApproved(entity: IAttachments): boolean {
		let attachments = entity.attachments;
		switch (attachments.length) {
			case 0: {
				return true;
			}
			case 1: {
				return this.isApproved(attachments[0]);
			}
		}

		for (let attachment of attachments) {
			if (!this.isApproved(attachment)) {
				return false;
			}
		}
		return true;
	}

	static areValid(entity: IAttachments): boolean {
		let attachments = entity.attachments;
		switch (attachments.length) {
			case 0: {
				return true;
			}
			case 1: {
				return this.isValid(attachments[0]);
			}
		}

		for (let attachment of attachments) {
			if (!this.isValid(attachment)) {
				return false;
			}
		}
		return true;
	}

	static findExpired(entities: (IAttachments | undefined)[]): AttachmentResult[];
	static findExpired(entity: IAttachments | undefined): AttachmentResult[];
	static findExpired(entities: IAttachments | undefined | ((IAttachments | undefined)[])): AttachmentResult[] {
		if (Array.isArray(entities)) {
			const attachments: AttachmentResult[] = [];
			for (const entity of entities) {
				attachments.push(...findAllInvalid(entity));
			}

			return attachments;
		}

		return findAllInvalid(entities);
	}

	static findExpiredTypes(entity: IAttachments | undefined, validAttachmentTypes: AttachmentType[]): AttachmentType[] {
		if (entity == null) return validAttachmentTypes;

		const result: AttachmentType[] = [];

		for (const type of validAttachmentTypes) {
			const current = entity.attachments.find(x => x.type === type);
			if (current == null || isInvalidInternal(current)) {
				result.push(type);
			}
		}

		return result;
	}

	static findSubmitted(entity: IAttachments | undefined): AttachmentType[] {
		if (entity == null) return [];
		const result: AttachmentType[] = [];
		for (const x of entity.attachments) {
			if (x.status === AttachmentStatus.Submitted) {
				result.push(x.type);
			}
		}
		return result;
	}

	static getStatus(entity: IAttachments): AttachmentStatus {
		const attachments = entity.attachments;

		switch (attachments.length) {
			case 0:
				return AttachmentStatus.Undefined;

			case 1:
				return attachments[0].status;

			default: {
				let hasExpired = false;
				let hasSubmitted = false;
				let hasAboutToExpire = false;
				for (const attachment of attachments) {
					if (attachment.status === AttachmentStatus.Rejected) {
						return AttachmentStatus.Rejected;
					}
					if (attachment.status === AttachmentStatus.Expired) {
						hasExpired = true;
					}
					if (attachment.status === AttachmentStatus.Submitted) {
						hasSubmitted = true;
					}
					if (attachment.status === AttachmentStatus.AboutToExpire) {
						hasAboutToExpire = true;
					}
				}
				if (hasExpired) return AttachmentStatus.Expired;
				if (hasSubmitted) return AttachmentStatus.Submitted;
				//still valid, but the aggregate pill must warn instead of showing plain Approved
				if (hasAboutToExpire) return AttachmentStatus.AboutToExpire;
				return AttachmentStatus.Approved;
			}
		}
	}

	static isApproved(attachment: AttachmentResult): boolean {
		return attachment.status === AttachmentStatus.Approved;
	}

	static isValid(attachment: AttachmentResult): boolean {
		//AboutToExpire is still valid TODAY (mirrors PermitStatusType: Active || AboutToExpire = active)
		return attachment.status === AttachmentStatus.Submitted || attachment.status === AttachmentStatus.Approved || attachment.status === AttachmentStatus.AboutToExpire;
	}

	static list(allPermits: IAllPermitsReservations): EntityDocument[] {
		const holders = mapAttachmentsToEntityDocuments(allPermits.holder, "holder");

		const residencies = createAttachmentEntries(allPermits.residencies, "residency");
		const drivers = createAttachmentEntries(allPermits.drivers, "driver");
		const permits = createAttachmentEntries(allPermits.permits, "permit");
		const reservations = createAttachmentEntries(allPermits.reservations, "reservation");
		const vehicles = createAttachmentEntries(allPermits.vehicles, "vehicle");

		return [...holders, ...residencies, ...drivers, ...permits, ...reservations, ...vehicles];
	}

	static listForPermits(allPermits: IAllPermitsReservations, permits: PermitResult[]): EntityDocument[] {
		const result: EntityDocument[] = mapAttachmentsToEntityDocuments(allPermits.holder, "holder");
		const processedAttachmentIds = new Set<string>();

		for (const permit of permits) {
			const permitAttachments = mapAttachmentsToEntityDocuments(permit, "permit");

			for (const permitDoc of permitAttachments) {
				if (!processedAttachmentIds.has(permitDoc.attachment.id)) {
					result.push(permitDoc);
					processedAttachmentIds.add(permitDoc.attachment.id);
				}
			}

			const currentDriver = Drivers.getValid(allPermits, permit.driver);
			if (currentDriver) {
				const driver = currentDriver.entity;
				const driverAttachments = mapAttachmentsToEntityDocuments(driver, "driver");

				for (const driverDoc of driverAttachments) {
					if (!processedAttachmentIds.has(driverDoc.attachment.id)) {
						result.push(driverDoc);
						processedAttachmentIds.add(driverDoc.attachment.id);
					}
				}
			}

			const validResidency = Residencies.getValid(allPermits, permit.residency);
			if (validResidency) {
				const residency = validResidency.entity;
				const residencyAttachments = mapAttachmentsToEntityDocuments(residency, "residency");
				for (const residencyDoc of residencyAttachments) {
					if (!processedAttachmentIds.has(residencyDoc.attachment.id)) {
						result.push(residencyDoc);
						processedAttachmentIds.add(residencyDoc.attachment.id);
					}
				}
			}

			const validVehicles = Vehicles.getValid(allPermits, permit.vehicles);
			for (const vehicleRef of validVehicles) {
				const vehicle = vehicleRef.entity;
				if (!vehicle) {
					continue;
				}

				const vehicleAttachments = mapAttachmentsToEntityDocuments(vehicle, "vehicle");
				for (const vehicleDoc of vehicleAttachments) {
					if (!processedAttachmentIds.has(vehicleDoc.attachment.id)) {
						result.push(vehicleDoc);
						processedAttachmentIds.add(vehicleDoc.attachment.id);
					}
				}
			}
		}

		return result;
	}
}

export {Attachments};
